/**
 * TaskTypeAutomationForm — form pra criar/editar automação de TASK TYPE.
 *
 * Sprint 3 / BUG-V2-058 (extraído de dialog interno duplicado em
 * `TipoAtividadeAutomacoesPage`).
 *
 * Sprint 3.1 (2026-05-26): migrado pra `react-hook-form` + `AcoesEditor`
 * compartilhado (com subset 3 ações).
 *
 * **Sprint 3.2 (2026-05-26): PARIDADE TOTAL.** Engine
 * `task-type-automations-engine.ts` agora delega execução pras 10 actions
 * novas via `automations-engine.executeAction` — resolve card host via
 * `task.card_id` e constrói DealSnapshot mínimo. Subset removido; AcoesEditor
 * mostra todas 13 ações. Backend Zod (`actionSchema` shared em
 * `automation-action-schemas.ts`) valida shape canônico.
 *
 * Backend separado de `automations`: continua em `/api/v1/task-type-automations`
 * (tabela `task_type_automations`). Engine separado também — mas reusa
 * executeAction pro caso comum.
 *
 *   - Triggers: 4 (on_create / on_complete / on_overdue / on_assigned)
 *   - Actions: 13 (paridade com Workflow + Questionário)
 */

import { useEffect, useMemo } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
  Label,
  Select,
} from '@/components/ui';
import type {
  CreateTaskTypeAutomationInput,
  TaskTypeAction,
  TaskTypeAutomation,
  TaskTypeTrigger,
} from '@/lib/task-type-automations-store';

import type { AutomationContexto } from '../types';
import { AcoesEditor } from '../AcoesEditor';
import { AutomationDialogHeaderByContext } from '../AutomationDialogHeaderByContext';

const TRIGGER_LABELS: Record<TaskTypeTrigger, string> = {
  on_create: 'Quando criado',
  on_complete: 'Quando concluído',
  on_overdue: 'Quando atrasado',
  on_assigned: 'Quando responsável atribuído',
};

// Sprint 3.2: schema permissivo de actions — AcoesEditor garante shape via
// defaultActionForType (todas 13). Backend Zod (server/lib/automation-action-schemas.ts)
// valida discriminated union strict.
const actionSchema = z.object({ type: z.string() }).passthrough();

const formSchema = z.object({
  nome: z.string().trim().min(1, 'Nome obrigatório').max(120),
  trigger: z.enum(['on_create', 'on_complete', 'on_overdue', 'on_assigned']),
  actions: z.array(actionSchema).min(1, 'Pelo menos 1 ação').max(20),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

export interface TaskTypeAutomationFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateTaskTypeAutomationInput) => Promise<void>;
  editing?: TaskTypeAutomation | null;
  busy?: boolean;
  /** Contexto kind='task_type' (required). */
  contexto: Extract<AutomationContexto, { kind: 'task_type' }>;
}

export function TaskTypeAutomationForm({
  open,
  onClose,
  onSubmit,
  editing,
  busy = false,
  contexto,
}: TaskTypeAutomationFormProps) {
  const activityTypeId = contexto.activityTypeId;

  const defaultValues: FormValues = useMemo(
    () => ({
      nome: editing?.nome ?? '',
      trigger: editing?.trigger ?? 'on_create',
      actions:
        (editing?.actions as unknown as FormValues['actions']) ??
        [{ type: 'notify_in_app', target: 'responsavel', message: '' }],
      enabled: editing?.enabled ?? true,
    }),
    [editing],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    defaultValues,
    mode: 'onChange',
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (open) reset(defaultValues);
  }, [open, defaultValues, reset]);

  async function submitHandler(values: FormValues) {
    await onSubmit({
      activityTypeId,
      nome: values.nome,
      trigger: values.trigger,
      actions: values.actions as unknown as TaskTypeAction[],
      enabled: values.enabled,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <AutomationDialogHeaderByContext
          contexto={contexto}
          isEditing={!!editing}
          editingNome={editing?.nome}
        />
      </DialogHeader>
      <form onSubmit={handleSubmit(submitHandler)} data-testid="task-type-automation-form">
        <DialogContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="tta-nome" required>
                Nome
              </Label>
              <Input
                id="tta-nome"
                data-testid="tta-form-nome"
                placeholder="ex: Notificar responsável"
                {...register('nome')}
                error={errors.nome?.message}
              />
            </div>

            <div>
              <Label htmlFor="tta-trigger" required>
                Quando disparar
              </Label>
              <Select id="tta-trigger" data-testid="tta-form-trigger" {...register('trigger')}>
                {(Object.keys(TRIGGER_LABELS) as TaskTypeTrigger[]).map((t) => (
                  <option key={t} value={t}>
                    {TRIGGER_LABELS[t]}
                  </option>
                ))}
              </Select>
            </div>

            {/* Sprint 3.2: AcoesEditor compartilhado com TODAS 13 ações.
                Engine task-type-automations-engine.ts agora delega actions
                novas pro automations-engine via lookup do task.card_id host. */}
            <AcoesEditor
              form={form}
              name="actions"
              columns={[]}
              tokensHelpText={
                <>
                  Tokens disponíveis: <code>{'{targetId}'}</code> resolve pro ID
                  da atividade/task que disparou. Actions que afetam card (mover
                  coluna, tag, prioridade) operam no card host (task.card_id).
                </>
              }
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting || busy}>
            Cancelar
          </Button>
          <Button type="submit" loading={isSubmitting || busy} data-testid="tta-form-submit">
            {editing ? 'Salvar' : 'Criar'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
